import React, { useEffect, useState } from "react";
import { Check, Circle, Copy, Download, Eye, Save } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

/* --------------------------------------------------------------- Listing site
   The public page PorchLight builds for this listing. Off until the agent
   turns it on — a page nobody chose to publish is not a feature.            */

const SITE_LINKS = [
  { key: "tour_url", label: "3D tour", ph: "https://my.matterport.com/show/?m=…" },
  { key: "video_url", label: "Video", ph: "https://youtu.be/…" },
  { key: "floorplan_url", label: "Floor plan", ph: "https://…" },
  {
    key: "zillow_url",
    label: "Zillow listing",
    ph: "https://www.zillow.com/homedetails/…",
    // Say plainly what this does, because it looks like it should do more.
    note: "Adds a “View on Zillow” button to the page. It does not pull views or saves back.",
  },
];

const fmtLeadDate = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
    " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

export default function ListingSiteTab({ seller, setToast }) {
  const sellerId = seller?.id || seller?._id;
  const [site, setSite] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [addr, setAddr] = useState("");
  const [links, setLinks] = useState({});
  const [copied, setCopied] = useState(false);

  const adopt = (data) => {
    setSite(data);
    setAddr(data.address || "");
    setLinks({ ...(data.extras || {}) });
  };

  useEffect(() => {
    if (!sellerId) return;
    let alive = true;
    (async () => {
      try {
        const [s, l] = await Promise.all([
          api.get(`/admin/seller/${sellerId}/listing-site`),
          api.get(`/admin/seller/${sellerId}/listing-leads`),
        ]);
        if (!alive) return;
        adopt(s.data);
        setLeads(l.data.leads || []);
      } catch (err) {
        if (alive) setToast(formatApiError(err?.response?.data?.detail) || "Couldn't load the listing page");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    /* eslint-disable-next-line */
  }, [sellerId]);

  const put = async (body, what) => {
    setBusy(what);
    try {
      const { data } = await api.put(`/admin/seller/${sellerId}/listing-site`, body);
      adopt(data);
      return data;
    } catch (err) {
      setToast(formatApiError(err?.response?.data?.detail) || "Couldn't save that");
      return null;
    } finally { setBusy(""); }
  };

  const toggle = async () => {
    const turningOn = !site.enabled;
    // No confirm box here any more. It only appeared once you had
    // already decided to publish, and agreeing to it was one keystroke.
    // The checklist below the button says what is missing before you
    // click, the button stays disabled until the page has something on
    // it, and the server refuses either way.
    const data = await put({ enabled: turningOn }, "toggle");
    if (data) {
      setToast(turningOn
        ? "The page is live. Anyone with the link can see it."
        : "Page turned off. The link now returns Not Found.");
    }
  };

  const saveAddress = async () => {
    const next = addr.trim();
    if (!next || next === site.address) return;
    if (!window.confirm(
      "Changing the address changes the web address too, so any link you've already shared will stop working. Continue?"
    )) { setAddr(site.address || ""); return; }
    const data = await put({ address: next }, "addr");
    if (data) setToast("Address updated. Share the new link — the old one is dead.");
  };

  const saveLinks = async () => {
    const data = await put(SITE_LINKS.reduce((a, l) => ({ ...a, [l.key]: links[l.key] || "" }), {}), "links");
    if (!data) return;
    // The backend drops anything that isn't http(s) — say so rather than
    // letting a typo quietly vanish.
    const dropped = SITE_LINKS.filter((l) => (links[l.key] || "").trim() && !data.extras[l.key]);
    setToast(dropped.length
      ? `Saved. ${dropped.map((d) => d.label).join(" and ")} needs to start with http:// or https:// — that one wasn't kept.`
      : "Links saved.");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(site.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setToast("Couldn't copy automatically — select the link and copy it.");
    }
  };

  const exportLeads = () => {
    const head = ["Date", "Name", "Email", "Phone", "Already has an agent", "Message"];
    const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [head, ...leads.map((l) => [
      l.created_at, l.name, l.email, l.phone, l.working_with_agent ? "Yes" : "No", l.message,
    ])].map((r) => r.map(cell).join(",")).join("\r\n");
    // BOM so Excel opens accented names correctly.
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `inquiries-${site.slug}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ color: "var(--text-muted)", padding: 8 }}>Loading…</div>;
  if (!site) return null;

  const live = !!site.enabled;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }} data-testid="listing-site-tab">
      {/* ------------------------------------------------ status + share link */}
      <div className="card" style={{ padding: 24, maxWidth: 760 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ minWidth: 240 }}>
            <div className="overline">Listing page</div>
            <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, marginBottom: 0, maxWidth: 480 }}>
              A public page for this home, built from the listing form and the photos you've
              uploaded. It exists before the MLS listing does, so you can share it the day you
              take the listing.
            </p>
          </div>
          <button type="button" data-testid="site-toggle"
                  className={live ? "btn-ghost" : "btn-primary"}
              onClick={toggle} disabled={busy === "toggle" || (!live && !site.ready)} style={{ whiteSpace: "nowrap" }}>
            {busy === "toggle" ? "Saving…" : live ? "Turn page off" : "Publish page"}
          </button>
        </div>

        <div style={{
          display: "flex", alignItems: "center", gap: 8, marginTop: 18, padding: "10px 14px",
          borderRadius: 10, background: live ? "rgba(43,76,63,0.08)" : "var(--surface-alt)",
          color: live ? "var(--primary)" : "var(--text-muted)", fontSize: 13.5, fontWeight: 500,
        }} data-testid="site-status">
          <span style={{
            width: 8, height: 8, borderRadius: 4, flex: "0 0 auto",
            background: live ? "#2B7A4B" : "var(--text-muted)",
          }} />
          {live ? "Live — anyone with the link can see this page." : "Off — the link returns Not Found."}
        </div>
        {!live && !site.ready && (
          <div
            data-testid="publish-checklist"
            style={{
              marginTop: 12, padding: "12px 14px", borderRadius: 10,
              background: "var(--surface-alt)", fontSize: 13.5,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              Before this page can go live
            </div>
            {[
              [!!site.has_details, "The listing form has been filled in"],
              [site.photo_count > 0, "At least one photo has been uploaded"],
            ].map(([done, label]) => (
              <div
                key={label}
                style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "3px 0",
                  color: done ? "var(--text-muted)" : "var(--text)",
                }}
              >
                {done ? <Check size={15} /> : <Circle size={15} />}
                <span>{label}</span>
              </div>
            ))}
          </div>
        )}

        {live && (
          <div style={{ marginTop: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>Share this link</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input className="input-field" readOnly value={site.url} data-testid="site-url"
                     onFocus={(e) => e.target.select()}
                     style={{ flex: "1 1 300px", fontFamily: "ui-monospace, monospace", fontSize: 13 }} />
              <button type="button" className="btn-ghost" onClick={copy} data-testid="site-copy">
                <Copy size={14} /> {copied ? "Copied" : "Copy"}
              </button>
              <a className="btn-ghost" href={site.url} target="_blank" rel="noopener noreferrer"
                 style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
                <Eye size={14} /> Open
              </a>
            </div>
            {!site.pretty_enabled && (
              <p style={{ color: "var(--text-muted)", fontSize: 12.5, marginTop: 8, marginBottom: 0 }}>
                Share this exact link. It opens the full page, and Facebook and
                Instagram pull the cover photo and details from it for the preview.
              </p>
            )}
          </div>
        )}

        {!site.ready && (
          <div style={{
            marginTop: 16, padding: "12px 14px", borderRadius: 10, fontSize: 13.5,
            background: "rgba(184,92,71,0.08)", color: "var(--secondary)",
          }} data-testid="site-not-ready">
            {!site.has_details && <div>The seller hasn't completed the listing form yet — the page has no details to show.</div>}
            {!site.photo_count && <div>No photos uploaded. Add them on the Social Posts tab and they appear here automatically.</div>}
          </div>
        )}

        <div style={{ marginTop: 18 }}>
          <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>
            Address shown on the page{" "}
            <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>— also sets the web address</span>
          </label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input className="input-field" value={addr} data-testid="site-address"
                   onChange={(e) => setAddr(e.target.value)} style={{ flex: "1 1 300px" }} />
            <button type="button" className="btn-ghost" onClick={saveAddress}
                    disabled={busy === "addr" || !addr.trim() || addr.trim() === site.address}>
              <Save size={14} /> {busy === "addr" ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------- links */}
      <div className="card" style={{ padding: 24, maxWidth: 760 }}>
        <div className="overline">Extra links</div>
        <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, marginBottom: 16 }}>
          Each one becomes a button on the page. Leave it blank to hide it.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {SITE_LINKS.map((l) => (
            <div key={l.key}>
              <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>{l.label}</label>
              <input className="input-field" placeholder={l.ph} value={links[l.key] || ""}
                     data-testid={`site-${l.key}`}
                     onChange={(e) => setLinks((x) => ({ ...x, [l.key]: e.target.value }))} />
              {l.note && <p style={{ color: "var(--text-muted)", fontSize: 12.5, margin: "6px 0 0" }}>{l.note}</p>}
            </div>
          ))}
        </div>
        <button type="button" className="btn-primary" onClick={saveLinks}
                disabled={busy === "links"} style={{ marginTop: 16 }} data-testid="site-save-links">
          <Save size={14} /> {busy === "links" ? "Saving…" : "Save links"}
        </button>
      </div>

      {/* ----------------------------------------------------- traffic + leads */}
      <div className="card" style={{ padding: 0, maxWidth: 760, overflow: "hidden" }}>
        <div style={{
          padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)",
          display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8,
        }}>
          <span style={{ fontWeight: 500 }}>Visits and inquiries</span>
          {!!leads.length && (
            <button type="button" className="btn-ghost" onClick={exportLeads} data-testid="site-export">
              <Download size={14} /> Export CSV
            </button>
          )}
        </div>

        <div style={{ display: "flex", gap: 32, padding: "18px 20px", borderBottom: "1px solid var(--border)", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 600 }} data-testid="site-views">{site.views}</div>
            <div style={{ fontSize: 12, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-muted)" }}>Visits</div>
          </div>
          <div>
            <div style={{ fontSize: 26, fontWeight: 600 }} data-testid="site-leads">{site.leads}</div>
            <div style={{ fontSize: 12, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-muted)" }}>Inquiries</div>
          </div>
          <p style={{ color: "var(--text-muted)", fontSize: 12.5, margin: 0, flex: "1 1 240px", alignSelf: "center" }}>
            One visit per person per day, and link-preview bots don't count. The visit number
            feeds your seller's Listing views stat. The names below stay with you — your seller
            never sees them.
          </p>
        </div>

        {leads.length === 0 ? (
          <div style={{ padding: "22px 20px", color: "var(--text-muted)", fontSize: 14 }}>
            No inquiries yet. They land here and in your inbox the moment someone uses the form on the page.
          </div>
        ) : leads.map((l) => (
          <div key={l.lead_id} data-testid={`site-lead-${l.lead_id}`}
               style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 500 }}>{l.name}</div>
              <div style={{ color: "var(--text-muted)", fontSize: 13 }}>{fmtLeadDate(l.created_at)}</div>
            </div>
            <div style={{ fontSize: 13.5, marginTop: 2 }}>
              <a href={`mailto:${l.email}`} style={{ color: "var(--primary)" }}>{l.email}</a>
              {l.phone && <> · <a href={`tel:${l.phone}`} style={{ color: "var(--primary)" }}>{l.phone}</a></>}
              {l.working_with_agent && (
                <span style={{
                  marginLeft: 8, fontSize: 11.5, padding: "2px 7px", borderRadius: 999,
                  background: "var(--surface-alt)", color: "var(--text-muted)",
                }}>has an agent</span>
              )}
            </div>
            {l.message && (
              <div style={{
                marginTop: 8, padding: "10px 12px", background: "var(--surface-alt)",
                borderRadius: 10, fontSize: 14, whiteSpace: "pre-wrap",
              }}>{l.message}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
